/**
 * Accordion lot group key — mirrors dbo.TransactionHistory.contract_identifier parts:
 *   contract_code / security_type / security_subtype / option_type /
 *   contract_month / option_strike_price / option_expire_date
 * Prefer contract_identifier when present; else rebuild from UserTransactionHistory_Search
 * fields (that proc does NOT select contract_identifier).
 * Trade price and buy/sell side are part of the key: a parent header states one
 * Position at one Price, so lots that disagree on either must not merge.
 */

/** Port of dbo.GetOptionType(security_desc_line_1) — needed for Short Dated vs Monthly, etc. */
export function optionTypeFromDesc(securityDescLine1) {
  const name = String(securityDescLine1 ?? "");
  if (/CSO Consecutive/i.test(name)) return "Spread Consecutive";
  if (/CSO/i.test(name)) return "Spread";
  if (/Short Dated/i.test(name) || / SD /i.test(name) || / CDF /i.test(name) || / Cdf /i.test(name)) {
    return "Short Dated";
  }
  if (/Week /i.test(name) || /Weekly /i.test(name) || / WK[1-5] /i.test(name)) return "Weekly";
  if (/Monthly /i.test(name)) return "Monthly";
  if (/Mid-Curve /i.test(name)) return "Mid-Curve";
  if (/Average Price /i.test(name)) return "Average Price";
  if (/European-Style /i.test(name)) return "European-Style";
  return "Monthly";
}

/** Strike for grouping: normalized decimal string, or empty when not an option. */
export function strikeKeyPart(row) {
  const subtype = row?.security_subtype_code;
  const isOption = subtype === "C" || subtype === "P";
  if (!isOption) return ""; // futures: strike not applicable
  const n = Number(row?.option_strike_price);
  if (!Number.isFinite(n)) return "";
  return n.toFixed(8); // match SQL convert(decimal(15,8))
}

export function expireKeyPart(row) {
  const raw = row?.option_expire_date;
  if (raw == null || raw === "") return "na";
  const s = String(raw);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[1]}${m[2]}${m[3]}`; // yyyyMMdd like SQL style 112
  if (/^\d{8}$/.test(s)) return s;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "na";
  const y = d.getUTCFullYear();
  const mo = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${y}${mo}${day}`;
}

/** Rebuild contract_identifier-equivalent key from API row fields. */
export function lotContractKey(row) {
  if (row?.contract_identifier) return String(row.contract_identifier);
  return [
    row?.contract_code ?? "-",
    row?.security_type_code ?? "F",
    row?.security_subtype_code ?? "F",
    optionTypeFromDesc(row?.security_desc_line_1),
    String(row?.contract_month ?? "").slice(0, 6) || "-",
    strikeKeyPart(row) || "0.00000000",
    expireKeyPart(row),
  ].join("/");
}

export function lotGroupKey(row) {
  const contractKey = lotContractKey(row);
  const price = Number(row?.trade_price);
  return [
    contractKey,
    String(row?.buy_sell_code ?? ""),
    Number.isFinite(price) ? price.toFixed(8) : "",
    row.crop_profile_year ?? "",
    row.crop_profile_commodity_name ?? "",
    row.crop_profile_name ?? "",
  ].join("\0");
}

const sumField = (lots, field) => lots.reduce((acc, row) => acc + (Number(row[field]) || 0), 0);

/** Combine lots that share the group key; parent carries summed qty at the shared price. */
export function combinePositionLots(rows) {
  const groups = new Map();
  for (const row of rows) {
    const key = lotGroupKey(row);
    const lots = groups.get(key);
    if (lots) lots.push(row);
    else groups.set(key, [row]);
  }
  return Array.from(groups.values()).map((lots) => {
    const head = lots[0];
    return {
      head,
      lots,
      is_combined: lots.length > 1,
      quantity: sumField(lots, "quantity"),
      quantity_open: sumField(lots, "quantity_open"),
      quantity_accumulated: sumField(lots, "quantity_accumulated"),
      quantity_potential_remaining: sumField(lots, "quantity_potential_remaining"),
      trade_price: head.trade_price,
    };
  });
}
